import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, map } from 'rxjs';

import type { ApiEnvelope } from '../http/api-envelope';
import type { Project, ProjectInput } from './project.model';
import type { ProjectResponse } from './projects.contract';
import { toProject, toProjectPayload, toProjects } from './projects.mapper';

const BASE = '/workspaces/projects';

/**
 * Transport for workspace projects. Doubles as its DI token.
 *
 * Lives in core (not a projects feature) because the selector sits in the
 * application shell — layouts must not import features.
 */
@Injectable()
export abstract class ProjectsApi {
  abstract list(q?: string): Observable<readonly Project[]>;
  abstract get(id: string): Observable<Project>;
  abstract create(input: ProjectInput): Observable<Project>;
  abstract update(id: string, input: ProjectInput): Observable<Project>;
  abstract delete(id: string): Observable<void>;
}

/**
 * `/workspaces/projects` — Go `internal/projects`, clrCompaniesREST.
 */
@Injectable()
export class HttpProjectsApi extends ProjectsApi {
  private readonly http = inject(HttpClient);

  override list(q?: string): Observable<readonly Project[]> {
    const query = q?.trim() ?? '';

    return this.http
      .get<ApiEnvelope<readonly ProjectResponse[] | null>>(BASE, {
        params: query ? { q: query } : undefined,
      })
      .pipe(map((response) => toProjects(response.data)));
  }

  override get(id: string): Observable<Project> {
    return this.http
      .get<ApiEnvelope<ProjectResponse>>(`${BASE}/${encodeURIComponent(id)}`)
      .pipe(map((response) => toProject(response.data ?? {})));
  }

  override create(input: ProjectInput): Observable<Project> {
    return this.http
      .post<ApiEnvelope<ProjectResponse>>(BASE, toProjectPayload(input))
      .pipe(map((response) => toProject(response.data ?? {})));
  }

  override update(id: string, input: ProjectInput): Observable<Project> {
    return this.http
      .put<ApiEnvelope<ProjectResponse>>(
        `${BASE}/${encodeURIComponent(id)}`,
        toProjectPayload(input),
      )
      .pipe(map((response) => toProject(response.data ?? {})));
  }

  override delete(id: string): Observable<void> {
    return this.http
      .delete(`${BASE}/${encodeURIComponent(id)}`, { responseType: 'text' })
      .pipe(map(() => undefined));
  }
}
